import { StoreTypeMap, StoreTypeMapValues, Result, StoreInterfaces } from '@/classes'

import EventStore from '@/store/events'
import GroupStore from '@/store/groups'
import UsersStore from '@/store/users'
import ArticleStore from '@/store/articles'
import PostsStore from '@/store/posts'
import PlaceStore from '@/store/places'
import ToastsStore from '@/store/toasts'
import ConversationStore from '@/store/conversations'
import ConversationsMessagesStore from '@/store/conversations-messages'
import PagesStore from '@/store/pages'

export interface FetchFilters {
  [key: string]: any
}

export interface FetchOptions {
  force?: boolean
  page?: number
  per_page?: number
  populate?: string
  include?: string[]
  exclude?: string[]
  sort?: { [key: string]: number }
}

const useEntityFetch = function <T extends keyof StoreInterfaces>(
  type: T,
  store: any,
  options?: {
    fetch?: {
      path?: string
      method?: 'GET' | 'POST'
    },
    getOne?: {
      path?: string
      method?: 'GET' | 'POST'
    },
    get?: {
      path?: string
      method?: 'GET' | 'POST'
    },
    delete?: {
      path?: string
    },
    create?: {
      path?: string
    },
    update?: {
      path?: string
      method?: 'PATCH' | 'POST'
    }
  }
) {
  const toastsStore = ToastsStore()

  const onFetch = async (
    params: any,
    options?: {
      path?: string,
      method?: string,
      raw?: boolean
    }
  ) => {
    const response: Result<{ items: StoreInterfaces[T][], count: number }> = await fetchAPI(options?.path || 'entities/get', {
      ...params, type
    }, { method: (options?.method || 'POST') as any })

    if (!response.ok) throw Error(response.error ?? 'api-error')

    console.log(`FETCHED ${response.data.items.length} ${type} (${process.client ? 'CLIENT' : 'SERVER'})`)
      
    response.data.items.forEach(item => {
      store.setItem(item)
    })

    return {
      ...response.data,
      items: options?.raw ? response.data.items : response.data.items.map(i => i._id),
      count: response.data.count
    }
  }
    
  const fetch = async (
    id: string,
    params: Ref<{
      filters: FetchFilters,
      options?: FetchOptions
    }>,
    page?: Ref<number>,
    o?: {
      lazy?: boolean
      extras?: string[]
      retry?: boolean
      immediate?: boolean
      store?: boolean,
      sort?: (a: StoreTypeMap[T], b: StoreTypeMap[T]) => number
    }
  ) => {
    const previousPages: Ref<string[]> = ref([])
    const isLoaded = ref(false)
    const lastItems: Ref<any[]> = ref([])

    const extras: Ref<{ [key: string]: any }> = ref({})
  
    const result = computed(() => {
      const request = [ ...new Set([
        ...previousPages.value,
        ...(lastResponse.value?.items || [])
      ]) ]

      lastItems.value = (lastResponse.value?.items || [])

      if (o?.extras) {
        extras.value = o?.extras.reduce((result, extra) => ({
          ...result,
          [extra]: lastResponse.value[extra] ?? null
        }), {})
      }

      const items = store.getItems(request) as StoreTypeMap[T][]

      return o?.sort ? items.sort(o?.sort) : items
    })

    const count = computed(() => lastResponse.value?.count || 0)

    const { data: lastResponse, pending: isLoading, refresh, error } = useLazyAsyncData(
      type + '-' + id + page?.value + Math.random(),
      () => onFetch(
        params.value, (options?.fetch ?? options?.get)
      ),
      {
        watch: [params],
        default: () => [],
        immediate: o?.immediate === false ? false : true,
        lazy: process.client || (o?.lazy ?? true)
      }
    )

    // ERROR HANDLING

    const retried = ref(0)
    const retryTimeout = ref()

    const onClear = () => {
      clearTimeout(retryTimeout.value)
      retryTimeout.value = null
      error.value = null
    }

    if (process.client && o?.retry !== false) {
      watch(error, () => {
        if (error.value) {
          console.error(error.value)

          if (retryTimeout.value) onClear()

          retried.value += 1
          error.value = null

          retryTimeout.value = setTimeout(() => {
            if (retryTimeout.value) refresh()
          }, retried.value > 5 ? 10000 : (retried.value > 3 ? 5000 : 2000))
        }
      }, { immediate: true })
    }

    watch(isLoading, (v) => {
      if (!v) isLoaded.value = true
    }, { immediate: true })

    if (page) {
      watch(page, () => {
        if (!page?.value) {
          previousPages.value = []
          return
        }
        
        previousPages.value = result.value.map(i => i._id) || []
      })
    }
    
    return {
      isLoading,
      lastResponse,
      lastItems,
      isLoaded,
      onClear,
      extras,
      result,
      count,
      refresh
    }
  }
  
  const fetchByIds = async (
    id: string,
    _ids: ComputedRef<string[]>,
    params?: FetchOptions,
    o?: {
      isDisabled?: boolean
      immediate?: boolean
      lazy?: boolean,
      refresh?: boolean
      retry?: boolean
      onFetched?: () => void
    }
  ) => {
    const toFetch: Ref<string[]> = ref([])
    const isLoaded = ref(false)

    const notFound: Ref<Map<string, Date>> = ref(new Map())

    const result = computed(() => store.getItems(_ids.value) as StoreTypeMap[T][])
    const count = computed(() => lastResponse.value?.count || 0)

    watch(_ids, () => {
      toFetch.value = o?.refresh ? _ids.value : _ids.value.map(_id => {
        return store.getItem(_id) ? null : _id
      }).filter(i => i) as string[]

    }, { immediate: true })
  
    const { data: lastResponse, pending: isLoading, refresh, error } = useLazyAsyncData(type + '-' + _ids.value.join('') + id, async () => {
      if (o?.isDisabled) return { items: [], count: 0 }

      const _idsToFetch = [ ...toFetch.value.filter(_id => !notFound.value.get(_id)) ]
      if (_idsToFetch.length <= 0) return { items: [], count: 0 }

      return onFetch({
        filters: { _id: { $in: toFetch.value } },
        options: { per_page: 999, ...(params || {}) }
      }, options?.get)
    }, {
      watch: [ toFetch ],
      default: () => [],
      immediate: o?.immediate === false ? false : true,
      lazy: process.client || (o?.lazy ?? true)
    })

    watch(lastResponse, () => {
      if (lastResponse.value) {
        const notFoundItems = toFetch.value.filter(_id => !(lastResponse.value as any).items.find((i: any) => i?._id === _id))

        notFoundItems.forEach(_id => notFound.value.set(_id, new Date()))
      }

      if (o?.onFetched) o.onFetched()
    })

    // ERROR HANDLING

    const retried = ref(0)
    const retryTimeout = ref()

    const onClear = () => {
      clearTimeout(retryTimeout.value)
      retryTimeout.value = null
      error.value = null
    }

    if (process.client && o?.retry !== false) {

      watch(error, () => {
        if (error.value) {
          console.error(error.value)

          if (retryTimeout.value) onClear()

          retried.value += 1
          error.value = null
          notFound.value = new Map()

          retryTimeout.value = setTimeout(() => {
            if (retryTimeout.value) refresh()
          }, retried.value > 5 ? 10000 : (retried.value > 3 ? 5000 : 2000))
        }
      }, { immediate: true })
    }

    watch(isLoading, (v) => {
      if (!v) isLoaded.value = true
    }, { immediate: true })

    return {
      result, count, isLoading, onClear, isLoaded, refresh
    }
  }

  const fetchOneByProperty = async (
    propertyName: string,
    property: ComputedRef<string>,
    params?: FetchOptions,
    o?: {
      lazy?: boolean
      retry?: boolean
      raw?: boolean
      immediate?: boolean
      isDisabled?: boolean
      onFetched?: (result: StoreTypeMap[T] | null) => void
    }
  ) => {
    const isLoaded = ref(false)
    const result: Ref<StoreTypeMap[T] | null> = computed(() => {

      return store.getItem(property.value, propertyName) as StoreTypeMap[T]
    })

    const { data: lastResponse, pending: isLoading, refresh, error } = useLazyAsyncData(type + '-' + property.value + propertyName, async () => {
      if (!property.value || o?.isDisabled) return { items: [], count: 0 }

      return onFetch({
        filters: { [propertyName]: property.value },
        options: { per_page: 999, ...(params || {}) }
      }, {
        ...(options?.getOne ?? options?.get),
        raw: o?.raw
      })
    }, {
      watch: [ property ],
      default: () => null,
      immediate: o?.immediate === false ? false : true,
      lazy: process.client || (o?.lazy ?? true)
    })

    watch(lastResponse, () => {
      if (o?.onFetched) o.onFetched(result.value)
    }, { immediate: true })
    
    watch(isLoading, (v) => {
      if (!v) isLoaded.value = true
    }, { immediate: true })

    // ERROR HANDLING

    const retried = ref(0)
    const retryTimeout = ref()

    const onClear = () => {
      clearTimeout(retryTimeout.value)
      retryTimeout.value = null
      error.value = null
    }

    if (process.client && o?.retry !== false) {

      watch(error, () => {
        if (error.value && retried.value <= 10) {
          console.error(error.value)

          if (retryTimeout.value) onClear()

          retried.value += 1
          error.value = null

          retryTimeout.value = setTimeout(() => {
            if (retryTimeout.value) refresh()
          }, retried.value > 5 ? 10000 : (retried.value > 3 ? 5000 : 2000))
        }
      }, { immediate: true })
    }

    return {
      result, isLoading, onClear, refresh
    }
  }

  const update = async<P>(_id: string, form: P, o?: { populate?: string }) => {
    const response: Result<{ updated: StoreTypeMap[T]}> = await fetchAPI(options?.update?.path || 'entities', {
      form, type, _id, options: o
    }, { method: options?.update?.method ?? 'PATCH' })

    if (!response.ok || !response.data?.updated) {
      toastsStore.error(response)
      return { ok: false }
    }

    const createdItem = response.data.updated
    store.setItem(createdItem)

    toastsStore.add({ content: useNuxtApp().$i18n.t('common.editSuccess') })

    return {
      ok: true,
      data: new (StoreTypeMapValues[type] as any)(createdItem)
    }
  }

  const create = async <P>(form: P) => {
    const response: Result<StoreInterfaces[T]> = await fetchAPI(options?.create?.path ?? 'entities/create', {
      form, type
    }, { method: 'POST' })

    if (!response.ok || !response.data) {
      toastsStore.error(response)
      return response
    }

    const createdItem = response.data
    store.setItem(createdItem)

    toastsStore.add({ content: useNuxtApp().$i18n.t('common.editSuccess') })

    return response
  }

  const remove = async (_id: string) => {
    const response: Result<StoreInterfaces[T]> = await fetchAPI(options?.delete?.path || 'entities', { type, _id }, { method: 'DELETE' })

    if (!response.ok) return toastsStore.error(response)

    store.deleteItem(_id)

    toastsStore.add({ content: useNuxtApp().$i18n.t('common.deleteSuccess') })

    return {
      ok: true,
      data: {
        _id
      }
    }
  }

  return {
    fetchOneByProperty, fetchByIds, fetch, create, update, remove
  }
}

export const useDataFetch = () => {
  const eventStore = useEntityFetch('event', EventStore(), {
    get: {
      path: 'event/get'
    },
    update: {
      path: 'event/update'
    },
    delete: {
      path: 'event/delete'
    }
  })

  const groupStore = useEntityFetch('group', GroupStore(), {
    create: {
      path: 'group/create'
    },
    update: {
      path: 'group/update'
    },
    delete: {
      path: 'group/delete'
    }
  })

  const userStore = useEntityFetch('user', UsersStore(), {

  })

  const postsStore = useEntityFetch('post', PostsStore(), {
    get: {
      path: 'posts/get'
    },
    delete: {
      path: 'post'
    }
  })

  const articleStore = useEntityFetch('article', ArticleStore(), {

  })

  const placesStore = useEntityFetch('place', PlaceStore(), {
    getOne: {
      path: 'entities/get'
    },
    get: {
      path: 'places/get'
    },
  })

  const conversationStore = useEntityFetch('conversation', ConversationStore(), {
    get: {
      path: 'conversations/get'
    },
  })

  const conversationMessageStore = useEntityFetch('conversation-message', ConversationsMessagesStore())

  const pagesStore = useEntityFetch('page', PagesStore(), {
    get: {
      path: 'pages/get'
    },
    update: {
      path: 'pages/update',
      method: 'POST'
    },
  })

  return {
    fetchEvents: eventStore.fetch,
    fetchEventsByIds: eventStore.fetchByIds,
    fetchEventByProperty: eventStore.fetchOneByProperty,

    updateEvent: eventStore.update,
    removeEvent: eventStore.remove,

    //
    
    fetchGroups: groupStore.fetch,
    fetchGroupsByIds: groupStore.fetchByIds,
    fetchGroupByProperty: groupStore.fetchOneByProperty,

    createGroup: groupStore.create,
    updateGroup: groupStore.update,
    removeGroup: groupStore.remove,

    //

    fetchUsers: userStore.fetch,
    fetchUserByProperty: userStore.fetchOneByProperty,
    fetchUsersByIds: userStore.fetchByIds,

    updateUser: userStore.update,
    removeUser: userStore.remove,

    //
    
    fetchConversations: conversationStore.fetch,
    fetchConversationsByIds: conversationStore.fetchByIds,
    fetchConversationByProperty: conversationStore.fetchOneByProperty,
    
    //

    fetchMessages: conversationMessageStore.fetch,

    //

    fetchArticles: articleStore.fetch,
    fetchArticleByProperty: articleStore.fetchOneByProperty,
    createArticle: articleStore.create,
    updateArticle: articleStore.update,
    
    //

    fetchPosts: postsStore.fetch,
    fetchPostByProperty: postsStore.fetchOneByProperty,
    fetchPostsByIds: postsStore.fetchByIds,

    updatePost: postsStore.update,
    removePost: postsStore.remove,

    //

    fetchPlaces: placesStore.fetch,
    fetchPlacesByIds: placesStore.fetchByIds,
    fetchPlaceByProperty: placesStore.fetchOneByProperty,
    updatePlace: placesStore.update,


    //
    
    fetchPages: pagesStore.fetch,
    updatePage: pagesStore.update,
    createPage: pagesStore.create,
    fetchPageByProperty: pagesStore.fetchOneByProperty
    
  } as const
}